import React from 'react'
import { useDrop } from 'react-dnd'

import { setPlanningShiftTableLoading } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { setPlanningUnassignedTableLoading } from '@/entities/gridTables/slices/planningUnassignedTablesSlice'
import { processAssignToScenario } from '@/entities/gridTables/thunks/processAssignToScenario'
import { processUnassignOrder } from '@/entities/gridTables/thunks/processUnassignOrders'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { updatePlanningUnassignedTableState } from '@/entities/gridTables/thunks/updatePlanningUnassignedTableState'
import { fetchScenarios, ReverseStopTypeLookup, StopTypeEnum } from '@/entities/schedules'
import { StopType } from '@/entities/schedules/models/StopType'
import { GridTableDragItem, GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'
import { EntityId } from '@reduxjs/toolkit'

export type PlanningUnassignedTableDropZoneProps = {
  scenarioId: EntityId
}

export const PlanningUnassignedTableDropZone: React.FC<PlanningUnassignedTableDropZoneProps> = ({ scenarioId }) => {
  const { setToastContent } = useToastContext()
  const dispatch = useAppDispatch()

  // Droppable zone for moving orders to inbox
  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.INBOX_ORDER_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW],
    drop: async (item: GridTableDragItem) => {
      if (item.type === GridTableDragType.SHIFT_STOP_ROW) {
        // Validate that the drop items are orders
        for (const stop of item.rows) {
          if (stop.stopType !== StopTypeEnum.Order) {
            setToastContent({
              open: true,
              severity: 'error',
              title: 'Failed to unassign order',
              content: `${ReverseStopTypeLookup[stop.stopType as StopType]} cannot be moved to unassigned.`,
            })

            return
          }
        }

        // Receive orders from shift
        const orderIds = item.rows.map((stop) => stop.orderId)
        const sourceScenarioId = item.rows[0].scenarioId
        const isSameScenario = parseInt(sourceScenarioId) == scenarioId
        const shiftId = item.rows[0].shiftId

        // Set Loading State
        dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: true }))
        
        // Process Drop
        if (isSameScenario) {
          dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: true }))
          await dispatch(processUnassignOrder({ shiftId, orderIds }))
        } else {
          await dispatch(processAssignToScenario({ scenarioId, orderIds }))
        }
        
        // Fetch scenarios and update table data
        await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
        await Promise.allSettled([
          dispatch(updatePlanningShiftTableState({ shiftId: shiftId })),
          dispatch(updatePlanningUnassignedTableState({ scenarioId: scenarioId })),
        ])

        // Reset Loading State
        dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: false }))
        dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: false }))
      } else if (item.type === GridTableDragType.INBOX_ORDER_ROW || item.type === GridTableDragType.UNASSIGNED_ORDER_ROW) {
        const orderIds = item.rows.map((row) => row.orderId)

        // Set Loading State
        dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: true }))
        await dispatch(processAssignToScenario({ scenarioId, orderIds }))
        await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
        await Promise.allSettled([
          dispatch(updatePlanningUnassignedTableState({ scenarioId: scenarioId })),
        ])

        // Reset Loading State
        dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: false }))
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      isDragging: monitor.getItem() !== null && (
        monitor.getItemType() === GridTableDragType.SHIFT_STOP_ROW ||
        monitor.getItemType() === GridTableDragType.INBOX_ORDER_ROW
      ) || (
        // Only allow drop if the source unassigned table is not the same as the target unassigned table
        monitor.getItemType() === GridTableDragType.UNASSIGNED_ORDER_ROW &&
        monitor.getItem().rows.length > 0 &&
        monitor.getItem().rows[0].scenarioId !== scenarioId
      ),
    }),
  })

  return (
    <StyledDropZone ref={dropRef} $isOver={isOver} $isDragging={isDragging} />
  )
}

const StyledDropZone = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isOver' && prop !== '$isDragging',
}) <{ $isOver: boolean, $isDragging: boolean }>`
  display: ${({ $isDragging }) => ($isDragging ? 'block' : 'none')};
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.palette.custom.widgetDropTarget.backgroundColor};
  border-radius: ${pxToRem(8)};
  border: ${({ theme }) => theme.palette.custom.widgetDropTarget.border};
  opacity: ${({ $isOver }) => ($isOver ? 1 : 0)};
  transition: opacity 0.1s;
  padding: ${props => props.theme.typography.pxToRem(8)};
`
